// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { EntityId } from '@epilot/entity-client/dist'

import type { BLOCK_TYPE, UNITS } from '../enums'

import type { IBlock, BlockBaseSettings, IBlockField } from './base'

export enum MeterReadingBlockMode {
  /**
   * Standard meter reading block
   * when the meter configuration is not known
   */
  STANDARD_INPUT = 'standard-input',
  /**
   * Meter reading block with entity updating capabilities
   * when meter configuration can be known from an existing entity in epilot
   */
  ENTITY_UPDATE = 'entity-update'
}

export enum TARIFF_TYPE {
  /**
   * A one-tariff counter records a single energy consumption value, regardless of the time of day.
   * This is a standard electricity meter where the rate remains the same at all times.
   */
  ONE_TARIFF = 'one_tariff',
  /**
   * A two-tariff counter (also called a dual-rate meter) records
   * energy consumption separately for two different time periods, typically:
   *   - Day (Peak) Usage: Higher rate during peak hours.
   *   - Night (Off-Peak) Usage: Lower rate during off-peak hours. This type of meter helps users benefit from lower night-time electricity prices.
   */
  TWO_TARIFF = 'two_tariff',
  /**
   * A bidirectional counter (also called a net meter) is used for energy systems
   * where electricity can both be consumed from the grid and fed back into it,
   * such as with solar panels. It records:
   *   - In: Energy consumed from the grid.
   *   - Out: Energy exported back to the grid.
   */
  BI_DIRECTIONAL = 'bi_directional'
}

type MeterReadingFields = {
  /**
   * Date of reading
   */
  date: IBlockField
  /**
   * Name/Role of the reader
   */
  by: IBlockField
  /**
   * Reason of reading
   */
  reason: IBlockField
}

type MeterConfiguration = {
  /**
   * A Market Location ID (MaLo ID) is a unique identifier for an energy
   * market location where electricity or gas is supplied.
   */
  marketLocationId: IBlockField
  /**
   * A meter ID is unique identifier for the physical meter deployed in a location
   */
  meterId: IBlockField
  /**
   * Choice of type of counters between one-tariff, two-tariff or bidirectional counter
   * This array configured the ability to choose from.
   *
   * @counterTypeChoices cannot be empty and must have atleast 1 choice.
   */
  counterTypeChoices: [TARIFF_TYPE, ...TARIFF_TYPE[]]
  /**
   * The meter's unit of measurement
   */
  unit: UNITS
}

export interface StandardMeterReadingBlock extends IBlock {
  type: BLOCK_TYPE.METER_READING
  settings: BlockBaseSettings & {
    mode: MeterReadingBlockMode.STANDARD_INPUT
    meter: MeterConfiguration
    fields: MeterReadingFields
  }
}

export interface EntityUpdatingMeterReadingBlock extends IBlock {
  type: BLOCK_TYPE.METER_READING
  settings: BlockBaseSettings & {
    mode: MeterReadingBlockMode.ENTITY_UPDATE
    fields: MeterReadingFields
  }
}

export type MeterReadingBlock =
  | StandardMeterReadingBlock
  | EntityUpdatingMeterReadingBlock

export type MeterReadingEntryValue = {
  maloId?: string
  /**
   * Meter's entity id ({@link EntityId})
   * @example "1b03aace-fbe5-461f-a048-34e252e3a313"
   */
  meterId?: string
  /**
   * This actually means the tariff type associated with the meter, not the meter's type.
   * @see {@link TARIFF_TYPE}
   */
  meterType?: TARIFF_TYPE
  /** Whom read the meter */
  readBy?: string
  /**
   * When was the meter reading read
   *
   * Expected to include the time of the reading.
   * If a strign is provided, it should be in ISO 8601 format.
   */
  readingDate?: Date | string | null
  readingValue?: number | null
  feedInValue?: number | null
  feedOutValue?: number | null
  ntValue?: number | null
  htValue?: number | null
  reason?: string
}

export type MeterReadingDynamicEntryValue = {
  /**
   * Meter's entity id ({@link EntityId})
   * @example "1b03aace-fbe5-461f-a048-34e252e3a313"
   */
  meterId: string
  /** Whom read the meter */
  readBy?: string
  /**
   * When was the meter reading read
   *
   * Expected to include the time of the reading.
   * If a strign is provided, it should be in ISO 8601 format.
   */
  readingDate?: Date | string | null
  /**
   * Why was the meter reading taken
   */
  reason?: string
  /**
   * Keeps multiple readings for the same meter, if applicable.
   */
  readings?: MeterReadingReading[]
  /**
   * Identifier found on the front of the meter (e.g., meter number or serial number).
   *
   * This value comes directly from the Meter's entity and cannot be directly mutated by the end-user.
   */
  _meter_number?: string
}

export type MeterReadingReading = {
  /**
   * UUID of this reading
   * @example "3b226690-01a5-4a70-9f1c-d3fab2698580"
   */
  counterId: string
  /**
   * Reading value
   * @example 120
   */
  value: number
  /**
   * Unit for the reading value
   * @example "kWh"
   */
  unit?: UNITS
  /**
   * Unit for the reading value
   * @example "feed-in"
   * @example "feed-out"
   */
  direction: string
  /**
   * A given label
   * TODO: describe what type of label this is
   */
  _label?: string
}

export type MeterReadingEntriesValue = {
  meters: MeterReadingDynamicEntryValue[]
}

export type MeterReadingBlockValue =
  | MeterReadingEntryValue
  | MeterReadingEntriesValue
